import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { getAdminToken } from '../modules/admin/auth/_requests'

// ADMIN-ONLY: per-customer post-variation settings + "generate for all posts".
// Self-contained (fetches by account id) so it drops into the admin account
// page and the admin tools on the customer's campaigns page.

const API_URL = process.env.REACT_APP_API_URL
const cfg = () => {
  const t = getAdminToken()
  const headers: Record<string, string> = {}
  if (t) headers.Authorization = `Bearer ${t}`
  return { headers, withCredentials: true }
}
const base = (id: string) => `${API_URL}/admin/post-variations/account/${id}`

interface State {
  settings: { enabled?: boolean; count?: number; contact_mode?: string; bulk?: any }
  posts_total: number
  posts_with_text: number
  posts_covered: number
}

const MODES = [
  { v: 'mixed', label: 'מעורב (חלק עם קישור, חלק טלפון בלבד)' },
  { v: 'with_link', label: 'הכול עם קישור' },
  { v: 'phone_only', label: 'הכול טלפון בלבד' },
]

const PostVariationsSettingsPanel: React.FC<{ accountId: string }> = ({ accountId }) => {
  const [data, setData] = useState<State | null>(null)
  const [busy, setBusy] = useState(false)
  const [force, setForce] = useState(false)
  const timer = useRef<number | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await axios.get(base(accountId), cfg())
      setData(res.data)
      return res.data as State
    } catch (e) {
      console.error('Failed to load post variation settings:', e)
      return null
    }
  }, [accountId])

  // Poll while a bulk run is going.
  const poll = useCallback(async () => {
    const d = await load()
    if (d?.settings?.bulk?.status === 'running') timer.current = window.setTimeout(poll, 4000)
  }, [load])

  useEffect(() => {
    poll()
    return () => { if (timer.current) window.clearTimeout(timer.current) }
  }, [poll])

  const save = async (patch: any) => {
    setBusy(true)
    try {
      await axios.put(base(accountId), patch, cfg())
      await load()
    } catch (e: any) {
      alert('שמירה נכשלה: ' + (e?.response?.data?.error || e?.message))
    } finally {
      setBusy(false)
    }
  }

  const generateAll = async () => {
    if (!data) return
    const todo = force ? data.posts_with_text : data.posts_with_text - data.posts_covered
    if (!window.confirm(`ליצור וריאציות ב-AI ל-${todo} פוסטים? זה רץ ברקע (כ-20 שניות לפוסט, 3 במקביל).`)) return
    setBusy(true)
    try {
      await axios.post(`${base(accountId)}/generate-all`, { force }, cfg())
      if (timer.current) window.clearTimeout(timer.current)
      timer.current = window.setTimeout(poll, 1500)
    } catch (e: any) {
      alert('ההפעלה נכשלה: ' + (e?.response?.data?.error || e?.message))
    } finally {
      setBusy(false)
    }
  }

  if (!data) return null
  const s = data.settings || {}
  const bulk = s.bulk || {}
  const running = bulk.status === 'running'
  const pct = bulk.total ? Math.round(((bulk.done || 0) + (bulk.failed || 0) + (bulk.skipped || 0)) / bulk.total * 100) : 0

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, background: '#fff', direction: 'rtl' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e1e2d' }}>
            <i className="bi bi-shuffle" style={{ marginLeft: 6, color: '#7c3aed' }}></i>
            וריאציות לפוסטים
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
            כשפעיל, הבוט מפרסם בכל פעם וריאציה אקראית של הפוסט במקום הנוסח המקורי. {data.posts_covered}/{data.posts_with_text} פוסטים עם וריאציות מעודכנות.
          </div>
        </div>
        <div
          onClick={() => !busy && save({ enabled: !s.enabled })}
          title="הפעלת וריאציות ללקוח זה"
          style={{
            width: 44, height: 24, borderRadius: 12, position: 'relative', flexShrink: 0,
            background: s.enabled ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' : '#e4e6ef',
            cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1, transition: 'all 0.2s',
          }}
        >
          <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, right: s.enabled ? 2 : 22, transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, marginTop: 12, flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          כמה לכל פוסט:
          <select value={s.count || 4} disabled={busy} onChange={(e) => save({ count: Number(e.target.value) })} style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid #e2e8f0' }}>
            {[3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          פרטי קשר:
          <select value={s.contact_mode || 'mixed'} disabled={busy} onChange={(e) => save({ contact_mode: e.target.value })} style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid #e2e8f0' }}>
            {MODES.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
          </select>
        </label>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={generateAll} disabled={busy || running}
          style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #7c3aed 0%, #2b70fa 100%)', color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: running ? 'wait' : 'pointer', opacity: busy || running ? 0.6 : 1 }}>
          <i className="bi bi-stars" style={{ marginLeft: 6 }} />צור וריאציות לכל הפוסטים
        </button>
        <label style={{ fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} disabled={running} /> גם לפוסטים שכבר יש להם (יצירה מחדש)
        </label>
      </div>

      {bulk.status && bulk.status !== 'idle' && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#475569' }}>
          {running ? 'רץ…' : bulk.status === 'done' ? 'הסתיים' : 'נכשל'} · {pct}% · נוצרו {bulk.done || 0} · דולגו {bulk.skipped || 0} · נכשלו {bulk.failed || 0} מתוך {bulk.total || 0}
          {bulk.last_error ? <span style={{ color: '#b45309' }}> · שגיאה אחרונה: {bulk.last_error}</span> : null}
          <div style={{ height: 6, background: '#f1f1f4', borderRadius: 3, marginTop: 4, overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: '#7c3aed', transition: 'width 0.4s' }} />
          </div>
        </div>
      )}
    </div>
  )
}

export default PostVariationsSettingsPanel
